import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import intl from "react-intl-universal";
import { BAAS_STATEMENT_PAGE_SIZE, fetchAccountStatements } from "../../../Hooks/Transactions";
import { useFilter } from "../../../Hooks/useFilter";
import { useIbProductFeaturesStore } from "../../../Stores/ibProductFeaturesStore";
import { isIbProductEnabled } from "../../../Utils/ibProductFeatures";
import { PixCardsSkeleton } from "../DashboardSkeleton";
import { HomeActivityCharts } from "./HomeActivityCharts.jsx";
import {
  buildDailyPixSeries,
  resolvePixInOutFromItems,
} from "./homeActivityChartUtils";

const formatPeriodLabel = (startDate, endDate) => {
  if (!startDate && !endDate) return "";
  try {
    const start = startDate
      ? format(new Date(`${startDate}T00:00:00`), "dd/MM/yyyy")
      : "";
    const end = endDate
      ? format(new Date(`${endDate}T00:00:00`), "dd/MM/yyyy")
      : "";
    if (start && end && start === end) return start;
    if (start && end) return `${start} – ${end}`;
    return start || end;
  } catch {
    return "";
  }
};

export const PixCardsDashboard = () => {
  const [statementItems, setStatementItems] = useState([]);
  const [statementLoading, setStatementLoading] = useState(false);

  const pixEnabled = useIbProductFeaturesStore((state) =>
    isIbProductEnabled(state.features, "pix"),
  );

  const { getFilterObj } = useFilter();
  const homeFilterObj = getFilterObj("homeStatement") as Record<string, any>;

  const startDate =
    homeFilterObj?.start_date || format(new Date(), "yyyy-MM-dd");
  const endDate =
    homeFilterObj?.end_date || format(new Date(), "yyyy-MM-dd");

  const periodLabel = useMemo(
    () => formatPeriodLabel(startDate, endDate),
    [startDate, endDate],
  );

  const statementQueryKey = `${startDate}|${endDate}|${homeFilterObj?.sub_account_id || ""}`;

  useEffect(() => {
    if (!pixEnabled) return undefined;

    let cancelled = false;
    setStatementLoading(true);

    fetchAccountStatements({
      start_date: startDate,
      end_date: endDate,
      page: 1,
      page_size: BAAS_STATEMENT_PAGE_SIZE,
      fetch_all: true,
      max_items: 500,
    })
      .then((payload) => {
        if (cancelled) return;
        const raw = payload?.data?.items;
        setStatementItems(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {
        if (!cancelled) setStatementItems([]);
      })
      .finally(() => {
        if (!cancelled) setStatementLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pixEnabled, statementQueryKey, startDate, endDate]);

  const { entradaCents, saidaCents } = useMemo(
    () => resolvePixInOutFromItems(statementItems),
    [statementItems],
  );

  const dailySeries = useMemo(
    () =>
      buildDailyPixSeries(statementItems, {
        startDate,
        endDate,
      }),
    [statementItems, startDate, endDate],
  );

  if (!pixEnabled) {
    return null;
  }

  if (statementLoading && !statementItems.length) {
    return <PixCardsSkeleton count={3} />;
  }

  return (
    <div className="mb-5 flex w-full min-w-0 max-w-full flex-col gap-3">
      <div className="min-w-0">
        <h2 className="dashboard-section-label m-0">
          {intl.get("dashboard.activityCharts.sectionTitle")}
        </h2>
        {periodLabel ? (
          <p className="mt-1 m-0 text-xs text-gray-500 dark:text-gray-400">
            {periodLabel}
          </p>
        ) : null}
      </div>

      <HomeActivityCharts
        entradaCents={entradaCents}
        saidaCents={saidaCents}
        dailySeries={dailySeries}
        periodLabel={periodLabel}
        loading={statementLoading && !statementItems.length}
      />

      {entradaCents === 0 && saidaCents === 0 && !statementLoading ? (
        <p className="app-empty-state m-0 py-3 text-center text-xs">
          {intl.get("dashboard.activityCharts.noMovement")}
        </p>
      ) : null}
    </div>
  );
};
